import React from 'react';
import clsx from 'clsx';
import { cardMediaClasses, styled } from '@mui/material';
import { SelectableCardProps, cardClasses, selectableCardClasses } from './types';
import { Card } from './Card';
import { projectPrefix } from '../constants';
import { Blue, Border, Secondary } from '../color';
import getTransition from '../utilities/getTransition';

// NOTE: special icon is necessary as the normal icons have small padding in view box
function SelectSVG() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="1" y="1" width="30" height="30" rx="15" fill="white" stroke="currentColor" strokeWidth="2" />
      <path d="M11.8421 15.7546L13.4487 17.3612L19.3279 11.488C19.9784 10.8378 21.0317 10.8376 21.6821 11.488C22.3326 12.1385 22.3326 13.1916 21.6821 13.8422L14.6288 20.8955C13.9782 21.546 12.9251 21.546 12.2746 20.8955L9.48789 18.1088C8.83737 17.4583 8.83737 16.4051 9.48789 15.7546C10.1384 15.1041 11.1916 15.1041 11.8421 15.7546Z" fill="currentColor" />
    </svg>
  );
}

const SelectIcon = styled('div')(() => ({
  position: 'absolute',
  top: -14,
  right: -14,
  zIndex: 5,
  width: 32,
  height: 32,
  borderRadius: '50%',
}));

const Wrapper = styled(
  'div',
  {
    name: `${projectPrefix}SelectableCard`,
    slot: 'Root',
    shouldForwardProp: (prop) => ['fullHeight'].indexOf(String(prop)) === -1,
    overridesResolver: (_, styles) => styles.root,
  },
)<Pick<SelectableCardProps, 'fullHeight' | 'selected'>>(() => ({
  position: 'relative',
  boxSizing: 'border-box',
  [`&.${selectableCardClasses.outlined}`]: {
    padding: '1px',
  },
  [`&.${selectableCardClasses.elevation}`]: {
    padding: '2px',
  },
  [`&.${selectableCardClasses.fullHeight}`]: {
    height: '100%',
  },
  [`&.${selectableCardClasses.selected}`]: {
    padding: '0px',
    [`.${selectableCardClasses.selectedIcon}`]: {
      // TODO: make this Outline / primary states / selected
      color: Blue[100],
    },
    [`.${cardClasses.root}`]: {
      // TODO: make this Outline / primary states / selected
      border: `2px solid ${Blue[100]}`,
      backgroundColor: Secondary.surface.selected,
    },
    [`.${cardMediaClasses.media}`]: {
      borderColor: Blue[100],
    },
  },
  [`&.${selectableCardClasses.disabled}`]: {
    [`.${selectableCardClasses.selectedIcon}`]: {
      color: Border.variant.main,
      ...getTransition(['color'], 250),
    },
  },
}));

export function SelectableCard(props: SelectableCardProps) {
  const {
    wrapperProps, iconProps, children, ...cardProps
  } = props;

  return (
    <Wrapper
      {...wrapperProps}
      fullHeight={cardProps.fullHeight}
      selected={cardProps.selected}
      className={clsx(
        wrapperProps?.className,
        {
          [selectableCardClasses.fullHeight]: cardProps.fullHeight,
          [selectableCardClasses.disabled]: cardProps.disabled,
          [selectableCardClasses.selected]: cardProps.selected,
          [selectableCardClasses.brand]: cardProps.variant === 'brand',
          [selectableCardClasses.elevation]: cardProps.variant === 'elevation',
          [selectableCardClasses.outlined]: cardProps.variant === 'outlined',
        },
      )}
    >
      <SelectIcon
        aria-label={cardProps.selected ? 'Selected' : 'Not selected'}
        {...iconProps}
        className={clsx(
          iconProps?.className,
          selectableCardClasses.selectedIcon,
        )}
      >
        <SelectSVG />
      </SelectIcon>

      <Card
        {...cardProps}
        interactive
      >
        {children}
      </Card>
    </Wrapper>
  );
}

SelectableCard.defaultProps = {
  fullHeight: true,
  selected: false,
};
